// =============================================================================
// Promotions (Subscriptions & Access)
//
// Promotions are the CMS's merchandising decision on top of a Cleeng coupon:
// pick a coupon (created in the Cleeng console), assign it to offers *within its
// targeting rules*, set a display window, enable it. The discount + targeting
// are read live from Cleeng through the CMS backend (the browser never holds the
// publisher token). Status is derived server-side, not stored.
// =============================================================================

import { api } from './api'

export type PromotionStatus = 'DRAFT' | 'SCHEDULED' | 'LIVE' | 'EXPIRED'

export interface Promotion {
  tc_promotion_id: string
  code: string
  billing_provider: string
  coupon_code: string
  tc_name: string | null
  tc_description: string | null
  starts_at: string | null
  ends_at: string | null
  enabled: boolean
  status: PromotionStatus
  offer_codes: string[]
  created_at: string | null
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
}

export interface CouponConstraints {
  offer_types: string[]
  payment_type: string // all | initial | renewal
  offers: string[] // restricted offer ids ("S123", "S123_US")
  tags: string[]
}

export interface Coupon {
  code: string
  name: string
  discount: number // fraction 0.01–1 (0.25 == 25% off)
  status: string // active | deactivated | ended
  constraints: CouponConstraints
}

export interface CleengOffer {
  id: string
  long_id: string | null
  title: string | null
  type: string | null // subscription | pass | vod | live
  price_amount: number | null
  price_currency: string | null
  // Per-platform IAP product ids keyed by Cleeng's platform names
  // (apple/android/roku/amazon/samsung/vizio). An absent or empty value means
  // the offer isn't sold on that store — see constants/storePlatforms.
  app_store_product_ids: Record<string, string>
  // Cleeng's active flag. An inactive offer is dropped by ACE on every
  // platform, so it renders nowhere.
  active: boolean
  // How often the offer bills, e.g. YEAR / 1. Null for a one-time purchase.
  // ACE derives a plan's name and period suffix from this, so the editor shows
  // the same derived values as placeholders — see derivePlanLabels.
  billing_period_unit: string | null
  billing_period_count: number | null
  tags: string[]
}

// An existing enabled promotion that would conflict with the candidate — it
// shares at least one offer AND its window overlaps. A non-empty list blocks the
// save; two live discounts on one offer at once is not allowed.
export interface ConflictingPromotion {
  tc_promotion_id: string
  code: string
  coupon_code: string
  tc_name: string | null
  starts_at: string | null
  ends_at: string | null
  offers: string[] // the shared offers where the two overlap in time
}

export interface PromotionWriteInput {
  coupon_code: string
  billing_provider?: string
  starts_at?: string | null
  ends_at?: string | null
  enabled?: boolean
}

// Editing only changes the window and enabled flag; the coupon is fixed at create.
export type PromotionUpdateInput = Pick<
  PromotionWriteInput,
  'starts_at' | 'ends_at' | 'enabled'
>

export const fetchPromotions = () =>
  api.get<{ promotions: Promotion[] }>('/promotions')

export const fetchPromotion = (id: string) =>
  api.get<Promotion>(`/promotions/${id}`)

export const createPromotion = (data: PromotionWriteInput) =>
  api.post<Promotion>('/promotions', data)

export const updatePromotion = (id: string, data: PromotionUpdateInput) =>
  api.put<Promotion>(`/promotions/${id}`, data)

export const deletePromotion = (id: string) =>
  api.delete<{ deleted: boolean; tc_promotion_id: string }>(`/promotions/${id}`)

// Resolve a coupon → live discount + targeting rules (backs the coupon picker).
export const resolveCoupon = (couponCode: string) =>
  api.get<Coupon>(`/promotions/coupons/${encodeURIComponent(couponCode)}`)

// Live Cleeng offers for the picker. When couponCode is given, the backend
// returns only the offers that coupon covers — it is the single eligibility
// evaluator (offer_eligible in promotions_router), so the picker doesn't
// re-derive the rule client-side.
export const fetchPromotionOffers = (couponCode?: string) => {
  const params: Record<string, string> = {}
  if (couponCode) params.coupon_code = couponCode
  return api.get<{ offers: CleengOffer[] }>(
    '/promotions/offers',
    Object.keys(params).length ? params : undefined
  )
}

// Existing enabled promotions that would conflict with a candidate promotion
// (share an offer AND overlap its window). A non-empty result means the
// candidate can't be enabled as-is — the form uses it to block the save and name
// the offenders. Pass excludeId when editing so the promotion doesn't clash with
// its own stored row.
export const fetchConflicts = (candidate: {
  couponCode: string
  startsAt?: string | null
  endsAt?: string | null
  excludeId?: string | null
}) => {
  const params: Record<string, string> = { coupon_code: candidate.couponCode }
  if (candidate.startsAt) params.starts_at = candidate.startsAt
  if (candidate.endsAt) params.ends_at = candidate.endsAt
  if (candidate.excludeId) params.exclude_id = candidate.excludeId
  return api.get<{ conflicts: ConflictingPromotion[] }>('/promotions/conflicts', params)
}

// Was → now price for the paywall preview.
export const discountedPrice = (amount: number, discount: number): number =>
  Math.max(0, amount * (1 - discount))

export const formatMoney = (amount: number, currency?: string | null): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ''}`.trim()
  }
}
